/**
 * Who is signed in, in one shape, derived in one place.
 *
 * `.docs/08-web-foundation.md` §DRY watchlist: "`usePrincipal` is the only place
 * session → role derivation happens." The rules are the ones
 * `apps/api/src/modules/auth/resolvers.ts` applies on the other side of the wire,
 * and they are stated once here so a page never reads `session.user.role` and
 * decides for itself what that means:
 *
 *   - no session → `anonymous`. The router's guards turn that into `/login`.
 *   - `user.role` is a **studio tier** → `staff_user`, carrying which tier it is.
 *     Studio people hold no seat on any client; an account that somehow holds both
 *     is refused by the API at every door.
 *   - anything else → `client_user`, acting for the active organisation, at the
 *     role their `member` row gives them.
 *   - **impersonation is a client session.** When staff view a client's portal,
 *     the session is the *target* user's and `session.impersonatedBy` names the
 *     staff member. That is why `impersonatedBy` hangs off the client principal
 *     and not off the staff one, and why the shell can render the banner without
 *     asking a second question.
 *
 * An unrecognised `member.role` falls to `member`, the least-privileged role — the
 * same defensive read `resolvers.ts` `roleOf` makes, for the same reason: the
 * failure that matters is the one that grants too much.
 *
 * The principal reaches components through context rather than through Better
 * Auth's hooks, which is what lets `renderWithProviders(ui, { principal })` render
 * a page as an owner, as staff, or as an impersonating staff member with no server
 * and no cookie. `src/auth/session.ts` is what fills that context in the browser.
 */

import { createContext, use, type ReactNode } from 'react'

import { CLIENT_ROLES, studioRoleOf, type ClientRole, type StudioRole } from '@fareast/shared'

export type ClientUserPrincipal = {
  readonly kind: 'client_user'
  readonly userId: string
  readonly name: string
  readonly email: string
  /** The active organisation, which is this client's id. Null until one is chosen. */
  readonly clientId: string | null
  readonly role: ClientRole
  /** The staff user id, when this session is an impersonation. */
  readonly impersonatedBy: string | null
}

export type StaffUserPrincipal = {
  readonly kind: 'staff_user'
  readonly userId: string
  readonly name: string
  readonly email: string
  /**
   * Which studio tier — `manager` for the whole studio, `staff` for the task board
   * (session 35). `packages/shared`'s vocabulary, so this and the API's
   * `StaffUserPrincipal` cannot mean different things by the same word.
   */
  readonly role: StudioRole
}

export type AnonymousPrincipal = { readonly kind: 'anonymous' }

export type Principal = ClientUserPrincipal | StaffUserPrincipal | AnonymousPrincipal

export const ANONYMOUS: AnonymousPrincipal = { kind: 'anonymous' }

/** One membership, as the switcher and the tenant rules need it. */
export type Organization = { readonly id: string; readonly name: string; readonly slug: string }

export type SessionState = {
  readonly principal: Principal
  /** Every client this person holds a seat on. Empty for staff and for nobody. */
  readonly organizations: readonly Organization[]
}

export const NO_SESSION: SessionState = { principal: ANONYMOUS, organizations: [] }

/* ------------------------------------------------------------------ derivation */

/** What a session read gives us, narrowed to the fields the rules use. */
export type SessionShape = {
  readonly user: {
    readonly id: string
    readonly name?: string | null | undefined
    readonly email: string
    readonly role?: string | null | undefined
  }
  readonly session: {
    readonly activeOrganizationId?: string | null | undefined
    readonly impersonatedBy?: string | null | undefined
  }
}

/** The `member` row for the active organisation, when there is one. */
export type ActiveMemberShape = { readonly role: string } | null | undefined

/**
 * The rules above, as one pure function. Every branch of it is a named test in
 * `principal.unit.test.ts`; nothing else in the SPA reads a session field.
 */
export function principalOf(
  session: SessionShape | null | undefined,
  activeMember: ActiveMemberShape,
  organizations: readonly Organization[] = [],
): Principal {
  if (session == null) return ANONYMOUS

  const { user } = session
  const name = user.name ?? user.email

  /*
   * **Both tiers**, and this is the single most important line in session 35's SPA
   * work. It was `user.role === 'staff'`, and everything that did not match fell
   * through to the *client user* branch below — so promoting the studio's owner to
   * `manager` without changing this would have made him a client user, whom
   * `guardStudioPortal` sends to `/app?notice=staff-only`. Locked out by his own
   * promotion, with no way back through the product.
   *
   * `studioRoleOf` is `packages/shared`'s, and is what `modules/auth/resolvers.ts`
   * classifies with on the other side of the wire. An unrecognised value answers null
   * and falls through exactly as it always did.
   */
  const studioRole = studioRoleOf(user.role)
  if (studioRole !== null) {
    return { kind: 'staff_user', userId: user.id, name, email: user.email, role: studioRole }
  }

  return {
    kind: 'client_user',
    userId: user.id,
    name,
    email: user.email,
    clientId: activeClientId(session, organizations),
    role: roleOf(activeMember),
    impersonatedBy: session.session.impersonatedBy ?? null,
  }
}

/**
 * Which organisation this session is acting for.
 *
 * `resolvers.ts`, rules 1 to 3, restated on this side because the shell has to name
 * the client before any request has been made: the session's own choice when it is
 * still a membership; otherwise the single membership, because belonging to one
 * company is not a choice; otherwise nothing, and the switcher is how you fix it.
 */
export function activeClientId(
  session: SessionShape,
  organizations: readonly Organization[],
): string | null {
  const chosen = session.session.activeOrganizationId ?? null
  if (chosen !== null && organizations.some((organization) => organization.id === chosen)) {
    return chosen
  }
  // The list is empty either because it has not been read or because this person
  // holds no seat. In the first case the session's own answer is the better one.
  if (chosen !== null && organizations.length === 0) return chosen
  return organizations.length === 1 ? (organizations[0]?.id ?? null) : null
}

/** Better Auth's `member.role` is free text and may be a comma-separated list. */
function roleOf(activeMember: ActiveMemberShape): ClientRole {
  const first = activeMember?.role.split(',')[0]?.trim()
  return CLIENT_ROLES.find((role) => role === first) ?? 'member'
}

/* --------------------------------------------------------------------- context */

const SessionContext = createContext<SessionState | null>(null)

export function SessionProvider({
  value,
  children,
}: {
  readonly value: SessionState
  readonly children: ReactNode
}) {
  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSessionState(): SessionState {
  const state = use(SessionContext)
  if (state === null) {
    throw new Error(
      'useSessionState() outside a <SessionProvider>. The router root and test/render.tsx put one there.',
    )
  }
  return state
}

/** The only way a component learns who is signed in. */
export function usePrincipal(): Principal {
  return useSessionState().principal
}

/* ---------------------------------------------------------------- convenience */

/**
 * True when a staff member is looking at a client's portal.
 *
 * The one question two very different things ask: the shell, to render the banner,
 * and `useApiMutation`, to refuse a `money` call before it is sent
 * (`security/impersonation.ts` refuses it again on the server).
 */
export function isImpersonating(principal: Principal): boolean {
  return principal.kind === 'client_user' && principal.impersonatedBy !== null
}

/**
 * True for somebody who may administer the account: an owner or an admin.
 *
 * `security/permissions.ts` is the server's own table and this is the SPA's reading
 * of two rows of it — the two roles that may invite, change a role, and reveal a
 * credential. It is deliberately **not** the money rule: `DecisionCard.mayApprove`
 * answers "may this person approve a charge", which `security/approvals.ts` owns and
 * which the API refuses independently. The two happen to name the same roles today,
 * and coupling them would mean a change to one silently changing the other.
 *
 * Staff are not account administrators of a client they are looking at. What staff
 * have is their own door (`/api/studio/*`), and an impersonating staff member is a
 * `client_user` here with `impersonatedBy` set — which the money rule refuses and
 * this one does not, because reading a member list is not a decision.
 */
export function isAccountAdmin(principal: Principal): boolean {
  return (
    principal.kind === 'client_user' && (principal.role === 'owner' || principal.role === 'admin')
  )
}

/**
 * True for a studio **manager** — the tier that runs the whole studio.
 *
 * `security/permissions.ts` `isManager` is the server's and this is the SPA's reading
 * of the same one word, which is exactly the relationship `isAccountAdmin` above has
 * to the client role table.
 *
 * It is one predicate and **not** a `StudioAbilities` matrix, for the reason
 * `packages/shared/src/schemas/studio-roles.ts` gives at length: `manager ⊃ staff` is
 * a rank, and a matrix over a rank is an indirection in front of a boolean.
 *
 * The per-**action** half of the rule is deliberately not here at all. Which of the
 * 160 tools a tier may run arrives on the wire, as `studioRole` on each
 * `_catalogue` entry, because a list of twenty-one tool names in this file would be
 * the hand-written duplicate session 19 deleted, growing back on the studio side.
 */
export function isManager(principal: Principal): boolean {
  return principal.kind === 'staff_user' && principal.role === 'manager'
}

/**
 * True for a studio account at the **restricted** tier.
 *
 * Beside `isManager` rather than derived from it at each call site, because
 * `!isManager(p)` is true of every client user and of nobody at all — a page that
 * asked it would render the studio's refusal to a client. Two named predicates, and
 * neither is the other's negation.
 */
export function isRestrictedStaff(principal: Principal): boolean {
  return principal.kind === 'staff_user' && principal.role === 'staff'
}
